import {
  ActionIcon, Badge, Button, Card, Grid, Group, Modal, NavLink, NumberInput, Select, Stack, Table, Text,
  TextInput, Textarea, Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { type Schemas, api, call, hasPermission } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { ErrorAlert, Loading, notifyError, notifyOk } from "../../components/common";
import { RuleBuilder } from "./RuleBuilder";

type Tag = Schemas["TagDefinitionIn"];

type CountMode = NonNullable<Schemas["MachineTypeOut"]["count_mode"]>;
export const COUNT_MODES: { value: CountMode; label: string }[] = [
  { value: "good_and_reject_counters", label: "Good + reject counters" },
  { value: "total_and_reject_counters", label: "Total − reject counters" },
  { value: "cycle_counter", label: "Cycle counter × units per cycle" },
  { value: "manual", label: "Manual entry only" },
];
const CATEGORIES = ["state", "counter", "process", "energy", "quality", "alarm", "info"];
const ROLES = [
  "machine_status", "run_signal", "fault_signal", "setup_signal", "power_current", "alarm_code", "program",
  "cycle_counter", "good_counter", "reject_counter", "total_counter", "energy_meter", "power",
];

export function MachineTypesPage() {
  const { me } = useAuth();
  const canManage = hasPermission(me, "masterdata:manage");
  const [selected, setSelected] = useState<string | null>(null);
  const [importOpen, importModal] = useDisclosure();
  const types = useQuery({
    queryKey: ["machine-types"],
    queryFn: () => call(api.GET("/api/v1/machine-types", { params: { query: { include_inactive: true } } })),
  });
  useEffect(() => {
    if (!selected && types.data?.length) setSelected(types.data[0].id);
  }, [types.data, selected]);

  if (types.isLoading) return <Loading />;
  if (types.error) return <ErrorAlert error={types.error} />;
  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Title order={2}>Machine types</Title>
          <Text size="sm" c="dimmed">Reusable definitions: signals (tags), how production is counted and how the machine state is detected.</Text>
        </div>
        {canManage && <Button onClick={importModal.open}>Add machine type</Button>}
      </Group>
      <Grid>
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card withBorder p={4}>
            {types.data!.map((t) => (
              <NavLink key={t.id} active={t.id === selected} onClick={() => setSelected(t.id)} label={t.name}
                description={t.code} rightSection={!t.is_active && <Badge size="xs" color="gray">inactive</Badge>} />
            ))}
            {types.data!.length === 0 && <Text p="sm" size="sm" c="dimmed">No machine types yet — add one from the catalogue.</Text>}
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 9 }}>
          {selected && <TypeEditor key={selected} id={selected} canManage={canManage} />}
        </Grid.Col>
      </Grid>
      <Modal opened={importOpen} onClose={importModal.close} title="Add machine type" size="lg">
        <ImportForm onDone={(id) => { importModal.close(); setSelected(id); }} />
      </Modal>
    </Stack>
  );
}

function ImportForm({ onDone }: { onDone: (id: string) => void }) {
  const qc = useQueryClient();
  const catalogue = useQuery({ queryKey: ["catalog-types"], queryFn: () => call(api.GET("/api/v1/catalog/machine-types")) });
  const [template, setTemplate] = useState<string | null>("generic");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const tpl = catalogue.data?.find((t) => t.code === template);
  const save = useMutation({
    mutationFn: () => call(api.POST("/api/v1/machine-types", {
      body: { code, from_template: template, ...(name ? { name } : {}) },
    })),
    onSuccess: (t) => { notifyOk("Machine type created"); void qc.invalidateQueries({ queryKey: ["machine-types"] }); onDone(t.id); },
    onError: notifyError,
  });
  return (
    <Stack>
      <Select label="Start from template" data={(catalogue.data ?? []).map((t) => ({ value: t.code, label: t.name }))}
        value={template} onChange={(v) => { setTemplate(v); if (v && !code) setCode(v); }} />
      {tpl && (
        <Text size="sm" c="dimmed">
          {tpl.description} · {tpl.tags?.length ?? 0} tags · {COUNT_MODES.find((m) => m.value === tpl.count_mode)?.label}
        </Text>
      )}
      <Group grow>
        <TextInput label="Code" required description="lower case, e.g. cnc_lathe" value={code}
          onChange={(e) => setCode(e.currentTarget.value)} />
        <TextInput label="Name" placeholder={tpl?.name} value={name} onChange={(e) => setName(e.currentTarget.value)} />
      </Group>
      <Text size="xs" c="dimmed">Everything can be changed after creation.</Text>
      <Button disabled={!code || !template} loading={save.isPending} onClick={() => save.mutate()}>Create</Button>
    </Stack>
  );
}

function TypeEditor({ id, canManage }: { id: string; canManage: boolean }) {
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ["machine-type", id],
    queryFn: () => call(api.GET("/api/v1/machine-types/{machine_type_id}", { params: { path: { machine_type_id: id } } })),
  });
  const [form, setForm] = useState<Schemas["MachineTypeUpdate"]>({});
  const [rules, setRules] = useState<Schemas["StateConfig"]>({ rules: [], default_state: "idle", offline_after_s: 300 });
  const [tags, setTags] = useState<Tag[]>([]);
  useEffect(() => {
    if (!detail.data) return;
    const d = detail.data;
    setForm({ name: d.name, description: d.description, count_mode: d.count_mode, ideal_cycle_time_s: d.ideal_cycle_time_s,
      simulator_profile: d.simulator_profile });
    setRules(d.state_config ?? { rules: [], default_state: "idle", offline_after_s: 300 });
    setTags(d.tags.map(({ id: _id, ...t }) => t));
  }, [detail.data]);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["machine-type", id] });
    void qc.invalidateQueries({ queryKey: ["machine-types"] });
  };
  const saveSettings = useMutation({
    mutationFn: () => call(api.PATCH("/api/v1/machine-types/{machine_type_id}", {
      params: { path: { machine_type_id: id } }, body: { ...form, state_config: rules },
    })),
    onSuccess: () => { notifyOk("Settings saved"); refresh(); },
    onError: notifyError,
  });
  const saveTags = useMutation({
    mutationFn: () => call(api.PUT("/api/v1/machine-types/{machine_type_id}/tags", {
      params: { path: { machine_type_id: id } }, body: { tags },
    })),
    onSuccess: () => { notifyOk("Tags saved"); refresh(); },
    onError: notifyError,
  });
  const toggleActive = useMutation({
    mutationFn: (is_active: boolean) => call(api.PATCH("/api/v1/machine-types/{machine_type_id}", {
      params: { path: { machine_type_id: id } }, body: { is_active },
    })),
    onSuccess: refresh,
    onError: notifyError,
  });
  const setTag = (i: number, patch: Partial<Tag>) => setTags((ts) => ts.map((t, j) => (j === i ? { ...t, ...patch } : t)));

  if (detail.isLoading) return <Loading />;
  if (detail.error) return <ErrorAlert error={detail.error} />;
  const d = detail.data!;
  return (
    <Stack>
      <Card withBorder>
        <Group justify="space-between" mb="sm">
          <Title order={4}>{d.name} <Text span c="dimmed" size="sm">({d.code}{d.template_code ? `, from template ${d.template_code}` : ""})</Text></Title>
          {canManage && (
            <Button size="xs" variant="default" onClick={() => toggleActive.mutate(!d.is_active)}>
              {d.is_active ? "Deactivate" : "Activate"}
            </Button>
          )}
        </Group>
        <Stack gap="xs">
          <Group grow>
            <TextInput label="Name" disabled={!canManage} value={form.name ?? ""}
              onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
            <Select label="Production counted by" disabled={!canManage} data={COUNT_MODES}
              value={form.count_mode ?? null} onChange={(v) => setForm({ ...form, count_mode: v as typeof form.count_mode })} />
            <NumberInput label="Default ideal cycle time (s)" disabled={!canManage} min={0} decimalScale={3}
              value={form.ideal_cycle_time_s ?? ""}
              onChange={(v) => setForm({ ...form, ideal_cycle_time_s: v === "" ? null : Number(v) })} />
          </Group>
          <Textarea label="Description" disabled={!canManage} autosize minRows={1} value={form.description ?? ""}
            onChange={(e) => setForm({ ...form, description: e.currentTarget.value })} />
          <Text fw={500} size="sm">Machine-state rules</Text>
          <RuleBuilder value={rules} onChange={setRules} tags={tags} disabled={!canManage} />
          {canManage && <Button w="fit-content" loading={saveSettings.isPending} onClick={() => saveSettings.mutate()}>Save settings</Button>}
        </Stack>
      </Card>
      <Card withBorder>
        <Group justify="space-between" mb="xs">
          <Title order={5}>Tags ({tags.length})</Title>
          {canManage && (
            <Group gap="xs">
              <Button size="xs" variant="default" leftSection={<IconPlus size={14} />}
                onClick={() => setTags([...tags, { key: "", label: "", data_type: "number", category: "process" }])}>Add tag</Button>
              <Button size="xs" loading={saveTags.isPending} onClick={() => saveTags.mutate()}>Save tags</Button>
            </Group>
          )}
        </Group>
        <Table.ScrollContainer minWidth={860}>
          <Table>
            <Table.Thead>
              <Table.Tr><Table.Th>Key</Table.Th><Table.Th>Label</Table.Th><Table.Th>Type</Table.Th><Table.Th>Unit</Table.Th>
                <Table.Th>Category</Table.Th><Table.Th>Role</Table.Th><Table.Th /></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tags.map((t, i) => (
                <Table.Tr key={i}>
                  <Table.Td><TextInput size="xs" disabled={!canManage} value={t.key} onChange={(e) => setTag(i, { key: e.currentTarget.value })} /></Table.Td>
                  <Table.Td><TextInput size="xs" disabled={!canManage} value={t.label} onChange={(e) => setTag(i, { label: e.currentTarget.value })} /></Table.Td>
                  <Table.Td><Select size="xs" w={100} disabled={!canManage} data={["number", "bool", "text"]} value={t.data_type ?? "number"}
                    onChange={(v) => setTag(i, { data_type: (v ?? "number") as Tag["data_type"] })} /></Table.Td>
                  <Table.Td><TextInput size="xs" w={80} disabled={!canManage} value={t.unit ?? ""} onChange={(e) => setTag(i, { unit: e.currentTarget.value || null })} /></Table.Td>
                  <Table.Td><Select size="xs" w={110} disabled={!canManage} data={CATEGORIES} value={t.category ?? "process"}
                    onChange={(v) => setTag(i, { category: (v ?? "process") as Tag["category"] })} /></Table.Td>
                  <Table.Td><Select size="xs" w={150} clearable disabled={!canManage} data={ROLES} value={t.role ?? null}
                    onChange={(v) => setTag(i, { role: (v ?? null) as Tag["role"] })} /></Table.Td>
                  <Table.Td>{canManage && <ActionIcon variant="subtle" color="red" aria-label="Remove tag"
                    onClick={() => setTags(tags.filter((_, j) => j !== i))}><IconTrash size={16} /></ActionIcon>}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <Text size="xs" c="dimmed" mt="xs">
          Roles give a tag a meaning for the platform: state rules can refer to roles, and the counting method needs the
          matching counter roles. Unknown tags sent by a machine are still stored.
        </Text>
      </Card>
    </Stack>
  );
}
